import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { type CreditStatus, parseCreditStatus } from "@/lib/credit-limit";

export function useCreditLimitStatus(orgId: string | null) {
  const [status, setStatus] = useState<CreditStatus | null>(null);

  const refresh = useCallback(async () => {
    if (!orgId) {
      setStatus(null);
      return;
    }
    const { data, error } = await supabase.rpc("organization_credit_status", { p_org_id: orgId });
    if (error) {
      console.error("[credit-limit] status read failed:", error.message);
      return;
    }
    setStatus(parseCreditStatus(data));
  }, [orgId]);

  useEffect(() => {
    void refresh();
    if (!orgId) return;

    const channel = supabase
      .channel(`org-credit-limit-${orgId}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "organizations", filter: `id=eq.${orgId}` },
        () => { void refresh(); },
      )
      .subscribe();

    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      supabase.removeChannel(channel);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [orgId, refresh]);

  return { status, refresh };
}
